import type { Key } from 'react-aria-components';

import { Trans } from '@lingui/react/macro';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect } from 'react';
import { z } from 'zod';

import { Tabs } from '@/components/base/tabs/tabs';
import { useAnalytics } from '@/lib/analytics/use-analytics';

import { AppliedProjects } from './-components/applied-projects';
import { InvitedProjects } from './-components/invited-projects';
import { PastProjects } from './-components/past-projects';

const tabs: { id: MyProjectsSearchParams['tab']; label: string }[] = [
  { id: 'invited', label: 'Invited' },
  { id: 'applied', label: 'Applied' },
  { id: 'past', label: 'Past projects' },
];

const myProjectsSearchSchema = z.object({
  tab: z.enum(['invited', 'applied', 'past']).default('invited'),
});

export type MyProjectsSearchParams = z.infer<typeof myProjectsSearchSchema>;

export const Route = createFileRoute(
  '/participants/_authenticated/my-projects/',
)({
  validateSearch: myProjectsSearchSchema,
  component: MyProjectsPage,
});

function MyProjectsPage() {
  const navigate = Route.useNavigate();
  const searchParams = Route.useSearch();
  const analytics = useAnalytics();
  const currentTab = searchParams.tab;
  const { user } = Route.useRouteContext();

  const userId = user.id;

  useEffect(() => {
    analytics?.screen({
      name: 'myProjects',
      attributes: { tab: currentTab },
    });
  }, [analytics, currentTab]);

  const handleTabChange = useCallback(
    (tab: Key) => {
      if (tab !== 'invited' && tab !== 'applied' && tab !== 'past') {
        return;
      }

      void navigate({
        search: (prev) => ({
          ...prev,
          tab,
        }),
      });
    },
    [navigate],
  );

  return (
    <div className="mx-auto max-w-(--max-width-container) px-4 py-8 md:px-8">
      <h1 className="mx-0 mb-8 font-heading text-2xl font-semibold text-primary">
        <Trans>My Projects</Trans>
      </h1>

      <Tabs
        className={'h-full'}
        selectedKey={currentTab}
        onSelectionChange={(tab: Key) => {
          handleTabChange(tab);
        }}
      >
        <Tabs.List type="underline" size="md" items={tabs}>
          {(item) => (
            <Tabs.Item
              id={item.id}
              data-testid={`participant-my-projects-tab-${item.id}`}
            >
              {item.label}
            </Tabs.Item>
          )}
        </Tabs.List>

        <Tabs.Panel id="invited" className="mt-6 h-full">
          <InvitedProjects userId={userId} />
        </Tabs.Panel>

        <Tabs.Panel id="applied" className="mt-6 h-full">
          <AppliedProjects userId={userId} />
        </Tabs.Panel>

        <Tabs.Panel id="past" className="mt-6 h-full">
          <PastProjects userId={userId} />
        </Tabs.Panel>
      </Tabs>
    </div>
  );
}
